/**
 * The renewal accounts the channel exhibits show (CHV-001, CHV-002, CHV-004,
 * CHV-009, CHV-010, PIL-004, HRO-011 and PGE-001), as the application prints
 * them: the Renewals channel of Kilnbrook Software, a fictional software
 * company, its Renewal accounts data set and the saved views over it.
 *
 * The figures are the board's renewal fixture (product-fixtures.ts), so the
 * dashboard and board totals still reconcile: £843,000 across twelve
 * accounts, five on track, four to watch, three at risk. Every name is
 * invented for the board (the company, its people and its accounts); none is
 * a customer, prospect or demo tenant. renewal-records.test.ts checks every
 * printed value against the application's formatters.
 */

import type { FieldOption } from "@product/app/workspace/channels/[channelId]/record-lens-utils";
import type { ProvenanceActor } from "@product/components/app/runs/provenance-avatar-stack";
import type {
  WorkspaceChannel,
  WorkspaceField,
  WorkspaceSession,
} from "@product/lib/mlx-workspace-client";

import type { ShotChannelTable, ShotTableCell } from "@/components/product-shot/channel-table";
import type { ShotWorkspace } from "@/components/product-shot/workspace";

const DAY = 86_400_000;
const MINUTE = 60_000;

/** Thursday 3 September 2026, 09:12 London time: the workspace's "now". */
export const RENEWALS_NOW = Date.UTC(2026, 8, 3, 8, 12);

/* ------------------------------------------------------------------ */
/*  The schema                                                          */
/* ------------------------------------------------------------------ */

export const HEALTH_OPTIONS = {
  onTrack: { value: "on_track", label: "On track", color: "green" },
  watch: { value: "watch", label: "Watch", color: "amber" },
  atRisk: { value: "at_risk", label: "At risk", color: "red" },
} as const satisfies Record<string, FieldOption>;

/**
 * The pipeline in order, each stage in the accent the application derives
 * for its position (defaultFieldOptionColor), written out because the
 * derivation reads @mlx/contracts at runtime.
 */
export const STAGE_OPTIONS = {
  discovery: { value: "discovery", label: "Discovery", color: "sky" },
  validation: { value: "validation", label: "Validation", color: "violet" },
  commercial: { value: "commercial", label: "Commercial", color: "teal" },
  contracting: { value: "contracting", label: "Contracting", color: "amber" },
  renewed: { value: "renewed", label: "Renewed", color: "green" },
} as const satisfies Record<string, FieldOption>;

export type HealthKey = keyof typeof HEALTH_OPTIONS;
export type StageKey = keyof typeof STAGE_OPTIONS;

/** Board order: the pipeline from first conversation to renewed. */
export const RENEWAL_STAGE_ORDER: readonly StageKey[] = ["discovery", "validation", "commercial", "contracting", "renewed"];

/**
 * Each column's accent on the Board, as columnColor() assigns it: none of
 * the five labels names a state it recognises, so the palette applies in
 * option order.
 */
export const STAGE_BOARD_ACCENTS: Readonly<Record<StageKey, string>> = {
  discovery: "#0ea5e9",
  validation: "#8b5cf6",
  commercial: "#14b8a6",
  contracting: "#ec4899",
  renewed: "#6366f1",
};

function field(
  key: string,
  label: string,
  type: WorkspaceField["type"],
  config: NonNullable<WorkspaceField["config"]> = {},
): WorkspaceField {
  return { id: `fld_${key}`, key, label, type, required: key === "title", status: "active", config };
}

/** The Renewal accounts data set's fields, in the order the saved table view shows them. */
export const RENEWAL_FIELDS: readonly WorkspaceField[] = [
  field("title", "Title", "text", { primary: true }),
  field("health", "Health", "select", { options: Object.values(HEALTH_OPTIONS) }),
  field("annual_value", "Annual value", "currency", { currency: "GBP" }),
  field("renewal_date", "Renewal date", "date"),
  field("probability", "Renewal probability", "percent"),
  field("account_lead", "Account lead", "text"),
  field("next_step", "Next step", "text"),
  field("stage", "Stage", "select", { options: RENEWAL_STAGE_ORDER.map((key) => STAGE_OPTIONS[key]) }),
];

export function renewalField(key: string): WorkspaceField {
  const found = RENEWAL_FIELDS.find((candidate) => candidate.key === key);
  if (!found) throw new Error(`Renewal accounts have no ${key} field.`);
  return found;
}

/* ------------------------------------------------------------------ */
/*  The accounts                                                        */
/* ------------------------------------------------------------------ */

export type RenewalAccount = {
  id: string;
  title: string;
  health: HealthKey;
  annualValue: number;
  /** yyyy-mm-dd */
  renewalDate: string;
  /** Percent, 0 to 100. */
  probability: number;
  accountLead: string;
  nextStep: string;
  stage: StageKey;
};

/** The twelve accounts, by renewal date. */
export const RENEWAL_ACCOUNTS: readonly RenewalAccount[] = [
  { id: "rec_ren_quillon", title: "Quillon Learning", health: "onTrack", annualValue: 31_000, renewalDate: "2026-08-27", probability: 100, accountLead: "Iris Kemble", nextStep: "Schedule adoption review", stage: "renewed" },
  { id: "rec_ren_brisk", title: "Brisk & Tally Logistics", health: "onTrack", annualValue: 48_000, renewalDate: "2026-08-31", probability: 100, accountLead: "Tobias Rennard", nextStep: "Launch success plan", stage: "renewed" },
  { id: "rec_ren_cairnmoor", title: "Cairnmoor Grid", health: "atRisk", annualValue: 84_000, renewalDate: "2026-09-14", probability: 72, accountLead: "Iris Kemble", nextStep: "Resolve DPA redlines", stage: "contracting" },
  { id: "rec_ren_tidewright", title: "Tidewright Energy", health: "onTrack", annualValue: 96_000, renewalDate: "2026-09-22", probability: 86, accountLead: "Dara Quint", nextStep: "Finalise deployment region", stage: "contracting" },
  { id: "rec_ren_marrowby", title: "Marrowby Fleet", health: "watch", annualValue: 62_000, renewalDate: "2026-09-28", probability: 64, accountLead: "Nell Ashdown", nextStep: "Confirm expansion seats", stage: "commercial" },
  { id: "rec_ren_glimmerway", title: "Glimmerway Mobility", health: "atRisk", annualValue: 125_000, renewalDate: "2026-09-30", probability: 49, accountLead: "Nell Ashdown", nextStep: "Confirm security sponsor", stage: "commercial" },
  { id: "rec_ren_pennock", title: "Pennock Frayne Legal", health: "onTrack", annualValue: 69_000, renewalDate: "2026-10-01", probability: 82, accountLead: "Tobias Rennard", nextStep: "Issue final order form", stage: "commercial" },
  { id: "rec_ren_wrenhollow", title: "Wrenhollow Health", health: "atRisk", annualValue: 118_000, renewalDate: "2026-10-05", probability: 55, accountLead: "Dara Quint", nextStep: "Run evidence workshop", stage: "validation" },
  { id: "rec_ren_tansy", title: "Tansy & Rook Retail", health: "watch", annualValue: 54_000, renewalDate: "2026-10-12", probability: 58, accountLead: "Tobias Rennard", nextStep: "Validate returns metric", stage: "validation" },
  { id: "rec_ren_larchmere", title: "Larchmere Foods", health: "onTrack", annualValue: 73_000, renewalDate: "2026-10-18", probability: 78, accountLead: "Iris Kemble", nextStep: "Share ROI model", stage: "validation" },
  { id: "rec_ren_brakewell", title: "Brakewell Manufacturing", health: "watch", annualValue: 39_000, renewalDate: "2026-11-03", probability: 42, accountLead: "Nell Ashdown", nextStep: "Map ERP sources", stage: "discovery" },
  { id: "rec_ren_saltcote", title: "Saltcote Property", health: "watch", annualValue: 44_000, renewalDate: "2026-11-16", probability: 38, accountLead: "Dara Quint", nextStep: "Agree pilot scope", stage: "discovery" },
];

/** An account's values, keyed by field. */
export function renewalValues(account: RenewalAccount): Record<string, unknown> {
  return {
    title: account.title,
    health: HEALTH_OPTIONS[account.health].value,
    annual_value: account.annualValue,
    renewal_date: account.renewalDate,
    probability: account.probability,
    account_lead: account.accountLead,
    next_step: account.nextStep,
    stage: STAGE_OPTIONS[account.stage].value,
  };
}

const pounds = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** A calendar date as the application prints a date field: "14 Sep 2026". */
export function printedDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number) as [number, number, number];
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

/**
 * Each value as formatFieldValue prints it: whole pounds (every value is at
 * least £1,000), the date as "14 Sep 2026", the probability as a percentage.
 * Written out here because the application's formatter reads @mlx/contracts
 * at runtime; the test compares the two.
 */
export function renewalDisplays(account: RenewalAccount): Record<string, string> {
  return {
    title: account.title,
    health: HEALTH_OPTIONS[account.health].label,
    annual_value: pounds.format(account.annualValue),
    renewal_date: printedDate(account.renewalDate),
    probability: `${account.probability}%`,
    account_lead: account.accountLead,
    next_step: account.nextStep,
    stage: STAGE_OPTIONS[account.stage].label,
  };
}

/** A cell at rest, as EditableCell shows it: the printed value, a select as its badge. */
function cell(account: RenewalAccount, key: string): ShotTableCell {
  const display = renewalDisplays(account)[key];
  if (display === undefined) throw new Error(`${account.id} prints no ${key}.`);
  if (key === "health") return { display, option: HEALTH_OPTIONS[account.health], title: display };
  if (key === "stage") return { display, option: STAGE_OPTIONS[account.stage], title: display };
  return { display, title: display };
}

/* ------------------------------------------------------------------ */
/*  The channel and its views                                           */
/* ------------------------------------------------------------------ */

export const RENEWALS_CHANNEL = { id: "ch_renewals", name: "Renewals" } as const;
export const RENEWALS_DATA_SET_NAME = "Renewal accounts";

/** The saved views over the data set, as the view picker names them. */
export const RENEWAL_VIEWS = {
  table: "Renewal command",
  /** A second table view that keeps three of the eight fields. */
  glance: "Renewals at a glance",
  board: "Renewal stages",
  dashboard: "Renewal overview",
} as const;

/**
 * A saved table view over the accounts: the columns it shows, in order,
 * sorted by one of them. Every account is a row.
 */
export function renewalTable(
  keys: readonly string[] = RENEWAL_FIELDS.map((candidate) => candidate.key),
  options: { viewName?: string; sortKey?: string; accounts?: readonly RenewalAccount[] } = {},
): ShotChannelTable {
  const fields = keys.map(renewalField);
  const sortField = renewalField(options.sortKey ?? "renewal_date");
  return {
    channel: RENEWALS_CHANNEL,
    dataSetName: RENEWALS_DATA_SET_NAME,
    viewName: options.viewName ?? RENEWAL_VIEWS.table,
    fields,
    sortField,
    rows: (options.accounts ?? RENEWAL_ACCOUNTS).map((account) => ({
      id: account.id,
      title: account.title,
      discussionCommentCount: 0,
      cells: Object.fromEntries(keys.map((key) => [key, cell(account, key)])),
    })),
  };
}

/** The whole saved table view: every field, by renewal date. */
export const RENEWAL_TABLE: ShotChannelTable = renewalTable();

/** The accounts in one Board column, and the column's subtotal as the Board prints it. */
export function stageAccounts(stage: StageKey): readonly RenewalAccount[] {
  return RENEWAL_ACCOUNTS.filter((account) => account.stage === stage);
}

const boardTotal = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });

export function renewalStageSubtotal(stage: StageKey): string | null {
  const total = stageAccounts(stage).reduce((sum, account) => sum + account.annualValue, 0);
  return total > 0 ? boardTotal.format(total) : null;
}

/* ------------------------------------------------------------------ */
/*  The workspace around the channel                                    */
/* ------------------------------------------------------------------ */

export const RENEWALS_USER: ProvenanceActor = { label: "Iris Kemble", imageUrl: null, initials: "IK" };

function session(id: string, title: string, status: string, ageMinutes: number): WorkspaceSession {
  return {
    id,
    title,
    status,
    createdAt: RENEWALS_NOW - ageMinutes * MINUTE,
    updatedAt: RENEWALS_NOW - ageMinutes * MINUTE,
    visibility: "private",
  };
}

function channel(id: string, name: string, ageDays: number): WorkspaceChannel {
  return { id, name, status: "active", createdAt: RENEWALS_NOW - ageDays * DAY, updatedAt: RENEWALS_NOW - ageDays * DAY };
}

const SESSIONS: WorkspaceSession[] = [
  session("ses_renewals_at_risk", "Renewals at risk this month", "completed", 4),
  session("ses_seat_expansion", "Seat expansion by account", "running", 9),
  session("ses_support_escalations", "Support escalations this week", "completed", 55),
  session("ses_q3_invoicing", "Q3 invoicing check", "completed", 1 * 24 * 60),
  session("ses_board_pack", "Board pack figures", "completed", 2 * 24 * 60),
  session("ses_onboarding_handovers", "Onboarding handovers", "completed", 4 * 24 * 60),
];

/** Kilnbrook Software's workspace, open on its Renewals channel. */
export const RENEWALS_WORKSPACE: ShotWorkspace = {
  organisationName: "Kilnbrook Software",
  user: RENEWALS_USER,
  ownerId: "usr_iris",
  channels: [
    channel(RENEWALS_CHANNEL.id, RENEWALS_CHANNEL.name, 0),
    channel("ch_support", "Support", 1),
    channel("ch_finance", "Finance", 2),
    channel("ch_product", "Product", 3),
    channel("ch_people", "People", 9),
  ],
  sessions: SESSIONS,
  pinnedSessions: [session("ses_renewal_playbook", "Renewal playbook", "completed", 12 * 24 * 60)],
  unreadSessionIds: ["ses_support_escalations"],
  activeSessionId: "ses_renewals_at_risk",
  sessionTitle: "Renewals at risk this month",
  composerSources: [],
};
